import {
  DIGILOCKER_KYC_CALLBACK_MESSAGE,
  DIGILOCKER_OPENER_ORIGIN_STORAGE_KEY,
  type DigilockerKycCallbackPayload,
} from './digilocker-kyc-callback.messages';

function isLocalDigilockerOpenerHost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

export function isAllowedDigilockerOpenerOrigin(origin: string): boolean {
  const trimmed = origin.trim();
  if (!trimmed) {
    return false;
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }
    if (typeof window !== 'undefined') {
      return (
        isLocalDigilockerOpenerHost(parsed.hostname) ||
        parsed.origin === window.location.origin
      );
    }
    return isLocalDigilockerOpenerHost(parsed.hostname);
  } catch {
    return false;
  }
}

/** CartKyc / DigiLocker gateway return on user portal (e.g. /dashboard?type=digilocker&status=success). */
export function isCartkycDigilockerSuccessReturn(
  params: URLSearchParams,
): boolean {
  const type = params.get('type')?.trim().toLowerCase();
  const status = params.get('status')?.trim().toLowerCase();
  return type === 'digilocker' && status === 'success';
}

function resolveDigilockerOpenerOrigin(
  openerOriginFromQuery?: string | null,
): string {
  const fromQuery = openerOriginFromQuery?.trim();
  if (fromQuery) {
    return fromQuery;
  }
  const fromStorage = sessionStorage
    .getItem(DIGILOCKER_OPENER_ORIGIN_STORAGE_KEY)
    ?.trim();
  if (fromStorage) {
    return fromStorage;
  }
  return '';
}

/**
 * Notifies the loan-application tab and closes this window when possible.
 * @returns true if opener was notified (or close was attempted).
 */
export function completeDigilockerKycReturn(
  openerOriginFromQuery?: string | null,
): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const openerOrigin = resolveDigilockerOpenerOrigin(openerOriginFromQuery);

  const payload: DigilockerKycCallbackPayload = {
    type: DIGILOCKER_KYC_CALLBACK_MESSAGE,
    status: 'success',
  };

  const opener = window.opener;
  if (!opener || opener.closed) {
    return false;
  }

  const targets: string[] = [];
  if (openerOrigin && isAllowedDigilockerOpenerOrigin(openerOrigin)) {
    targets.push(openerOrigin);
  }
  if (!targets.length) {
    // Cross-origin (e.g. admin on localhost, return on UAT user portal): cannot read opener.location.
    targets.push('*');
  }

  for (const target of targets) {
    try {
      opener.postMessage(payload, target);
      opener.focus();
      sessionStorage.removeItem(DIGILOCKER_OPENER_ORIGIN_STORAGE_KEY);
      scheduleDigilockerReturnWindowClose();
      return true;
    } catch {
      /* try next target */
    }
  }

  return false;
}

function scheduleDigilockerReturnWindowClose(): void {
  window.setTimeout(() => {
    try {
      window.close();
    } catch {
      /* ignore */
    }
  }, 350);
}

/** Run on app bootstrap when URL indicates DigiLocker success return. */
export function tryHandleDigilockerKycReturnOnLoad(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  if (!isCartkycDigilockerSuccessReturn(params)) {
    return false;
  }
  return completeDigilockerKycReturn(params.get('openerOrigin'));
}
